import { validateMeasurement, triggerActive } from '@icontactic/logic';

/**
 * F3E2-04: initial options straight from the purchase gate's server-rendered JSON, so the component's first
 * render does not depend on module execution order (the gate broadcasts `ict:options-change` only once).
 */
function initialOptionsFrom(root) {
  try {
    const payload = root.querySelector('ict-purchase-gate script[type="application/json"]');
    if (!payload) return null;
    const cfg = JSON.parse(payload.textContent || '{}');
    const names = cfg.optionNames || [];
    const values = cfg.selected?.options || [];
    const out = {};
    names.forEach((n, i) => { if (values[i] !== undefined) out[n] = values[i]; });
    return out;
  } catch { return null; }
}

/**
 * <ict-measurement> — conditional customer measurement (P08-style custom strap, docs/05b).
 * The input never changes price. The value is stored as a line-item property via the purchase gate.
 * Range validation is active only when min/max are supplied (0 = not set).
 */
class IctMeasurement extends HTMLElement {
  #active = true;
  #abort = new AbortController();

  connectedCallback() {
    this.input = this.querySelector('input[data-ict-measurement]');
    this.errorEl = this.querySelector('[data-ict-error]');
    this.trigger = { option: this.dataset.triggerOption || '', value: this.dataset.triggerValue || '' };
    this.property = this.dataset.property || 'Measurement';
    this.min = parseInt(this.dataset.min || '0', 10) || 0;
    this.max = parseInt(this.dataset.max || '0', 10) || 0;
    this.unit = this.dataset.unit || '';
    this.messages = JSON.parse(this.dataset.messages || '{}');
    this.touched = false;

    const { signal } = this.#abort;
    this.input?.addEventListener('input', () => { this.touched = true; this.#update(); }, { signal });
    this.input?.addEventListener('blur', () => { this.touched = true; this.#update(); }, { signal });
    const root = this.closest('[data-ict-scope]') || this.closest('.shopify-section') || document.body;
    root.addEventListener('ict:options-change', this.#onOptions, { signal });
    root.addEventListener('ict:gate-ready', () => this.#update(), { signal });
    const initial = initialOptionsFrom(root);
    if (initial) this.#active = triggerActive(this.trigger, initial);
    this.#update();
  }

  disconnectedCallback() { this.#abort.abort(); }

  #onOptions = (event) => {
    const active = triggerActive(this.trigger, event.detail?.options);
    if (active !== this.#active) {
      this.#active = active;
      if (!active && this.input) { this.input.value = ''; this.touched = false; }
      this.#update();
    }
  };

  get validation() {
    return validateMeasurement(this.input?.value, { min: this.min, max: this.max });
  }

  #message(code) {
    const raw = this.messages[code] || '';
    return raw.replace('{{ min }}', String(this.min)).replace('{{ max }}', String(this.max)).replace('{{ unit }}', this.unit);
  }

  #update() {
    this.hidden = !this.#active;
    this.classList.toggle('ict-hidden-important', !this.#active);
    const v = this.validation;
    if (this.input) {
      this.input.setAttribute('aria-invalid', String(this.#active && this.touched && !v.valid));
      this.input.required = this.#active;
    }
    if (this.errorEl) this.errorEl.textContent = this.#active && this.touched && !v.valid ? this.#message(v.code) : '';
    const properties = v.valid ? { [this.property]: String(v.value) } : {};
    this.dispatchEvent(
      new CustomEvent('ict:component-state', {
        bubbles: true,
        detail: {
          id: this.id,
          kind: 'measurement',
          active: this.#active,
          required: true,
          valid: v.valid,
          code: v.code,
          message: v.valid ? '' : this.#message(v.code),
          properties,
          trigger: this.trigger,
        },
      })
    );
  }
}

if (!customElements.get('ict-measurement')) customElements.define('ict-measurement', IctMeasurement);